import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { PencilLine, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
  AlertDialogAction,
} from "../ui/alert-dialog";
import { useState } from "react";
import { useEffect } from "react";
import { useUserId } from "./UserIdContext";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import { Button } from "../ui/button";

const API_URL = process.env.REACT_APP_API_URL; // Ensure this matches your backend URL
const UserDesign = () => {
  const navigate = useNavigate();
  const username = process.env.REACT_APP_API_USERNAME;
  const password = process.env.REACT_APP_API_PASSWORD;
  const credentials = `${username}:${password}`;
  const encodedCredentials = btoa(credentials); // Base64 encode the credentials

  const { userId } = useUserId();

  const [projectMap, setProjectMap] = useState({});

  const [deleteId, setDeleteId] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [designs, setDesigns] = useState([]);

  useEffect(() => {
    const username = process.env.REACT_APP_API_USERNAME;
    const password = process.env.REACT_APP_API_PASSWORD;
    const credentials = `${username}:${password}`;
    const encodedCredentials = btoa(credentials); // Base64 encode the credentials

    const fetchProjectDetails = async () => {
      if (!designs.length) return;

      const uniqueProjectIds = [
        ...new Set(designs.map((d) => d.project).filter(Boolean)),
      ];

      // console.log(uniqueProjectIds);

      const projectDataMap = {};

      for (const id of uniqueProjectIds) {
        try {
          const token = localStorage.getItem("x-auth-token");
          const response = await fetch(`${API_URL}/api/project/${id}`, {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Basic ${encodedCredentials}`,
              "x-auth-token": token,
            },
          });

          if (response.ok) {
            const result = await response.json();
            const project = result.data.data.project;

            projectDataMap[id] = {
              title: project.title,
              clientName: project.clientName,
            };
          }
        } catch (err) {
          console.error("Error fetching project:", err);
        }
      }

      setProjectMap(projectDataMap);
    };

    fetchProjectDetails();
  }, [designs]);

  useEffect(() => {
    const username = process.env.REACT_APP_API_USERNAME;
    const password = process.env.REACT_APP_API_PASSWORD;
    const credentials = `${username}:${password}`;
    const encodedCredentials = btoa(credentials); // Base64 encode the credentials

    const handleFetchDesignsByUser = async () => {
      try {
        const token = localStorage.getItem("x-auth-token");
        // console.log(token);
        const response = await fetch(`${API_URL}/api/designs/${userId}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${encodedCredentials}`, // Basic Auth header
            "x-auth-token": token,
          },
        });

        const responseData = await response.json();

        if (response.ok) {
          setDesigns(responseData.data.summary.designs || []);
          // console.log("Projects fetched:", responseData);
        } else {
          console.error("Failed to fetch projects:", responseData.message);
        }
      } catch (error) {
        console.error("Error fetching projects:", error);
      }
    };
    handleFetchDesignsByUser();
  }, [userId]);

  const handleReview = (requestData, id) => {
    navigate("/calculator", {
      state: {
        // projectId: projectId,
        requestData: {
          ...requestData,
          performRecalculation: true, //  Set recalculation mode
          existingCalculationId: id, // Include existing calculation ID
        },
      },
    });
  };

  const handleConfirmDelete = () => {
    if (deleteId) {
      document.activeElement?.blur(); // Prevent focus error
      handleDelete(deleteId); // Delete the item
    }
    setIsDialogOpen(false); // Close the dialog
    setTimeout(() => setDeleteId(null), 200); // Reset deleteId safely
  };

  const handleCancelDelete = () => {
    setIsDialogOpen(false); // Close the dialog
    setTimeout(() => setDeleteId(null), 200); // Reset deleteId safely
  };

  const handleDelete = async (id) => {
    if (!id) return;

    try {
      const token = localStorage.getItem("x-auth-token"); // Retrieve token
      const response = await fetch(`${API_URL}/api/calculate/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${encodedCredentials}`, // Basic Auth header
          "x-auth-token": token,
        },
      });

      if (response.ok) {
        setDesigns((prevData) => prevData.filter((item) => item._id !== id));
        setDeleteId(null); // Clear the ID after deletion
      } else {
        console.error("Failed to delete the item.");
      }
    } catch (err) {
      console.error("Error during deletion:", err);
    }
  };

  const exportToCSV = () => {
    if (!designs || designs.length === 0) {
      alert("No designs available to export.");
      return;
    }

    // Define the header row
    const headers = [
      "Project Title",
      "Client Name",
      "Pipe Section No.",
      "Pipe ID (in)",
      "SIPP Liner Length (ft)",
      "t for X1.6 (mm)",
      "Design t (mm)",
      "Install t (mm)",
      "Short-term Burst Pressure",
      "Design Assumes",
    ];

    // Convert designs into an array of rows
    const rows = designs.map((item, index) => [
      index + 1,
      item.internalDiameter ?? "NULL",
      item.lengthLined ?? "NULL",
      item.addedClassII?.tX1_6 ?? "NULL",
      item.addedClassII?.designTClassII ?? "NULL",
      item.addedClassII?.installTClassII ?? "NULL",
      item.addedClassII?.burstPressureII ?? "NULL",
      item.vacuumCondition ?? "NULL",
    ]);

    // Combine headers and rows
    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Designs");

    // Convert to a Blob and trigger download
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const data = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(data, "UserDesign.xlsx");
  };

  // console.log(userId)

  return (
    <main>
      <div className="tw-flex tw-justify-end tw-mb-4">
        <Button
          onClick={exportToCSV}
          className="tw-bg-[#00B5ED] tw-text-white tw-font-bold"
        >
          Download as Excel Sheet
        </Button>
      </div>

      {/* Class I Design Table */}
      <div className="tw-mb-12">
        <h2 className="tw-text-sm md:tw-text-base tw-font-bold tw-bg-[#00B5ED] tw-text-white tw-p-2 tw-uppercase tw-mb-2 tw-rounded-t-md">
          Class I Design
        </h2>
        <Table className="tw-pb-4 tw-border-collapse tw-border tw-border-black">
          <TableHeader>
            <TableRow className="tw-border tw-border-black">
              <TableHead className="tw-border tw-border-black">
                Project Title
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                Client Name
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                Pipe Section No.
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                Pipe ID (in)
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                SIPP Liner Length (ft)
              </TableHead>

              {/* <TableHead className="tw-border tw-border-black">
                      t for X1.6 (mm)
                    </TableHead> */}
              <TableHead className="tw-border tw-border-black">
                Design t (mm)
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                Install t (mm)
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                Short-term Burst Pressure (psi)
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                Design Assumes
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {designs.map((item, index) => (
              <TableRow key={index} className="tw-border tw-border-black">
                <TableCell className="tw-border tw-border-black">
                  {projectMap[item.project]?.title || "Loading..."}
                </TableCell>

                <TableCell className="tw-border tw-border-black">
                  {projectMap[item.project]?.clientName || "Loading..."}
                </TableCell>
                <TableCell className="tw-border tw-border-black">
                  {index + 1}
                </TableCell>
                <TableCell className="tw-border tw-border-black">
                  {item.internalDiameter ?? "NULL"}
                </TableCell>
                <TableCell className="tw-border tw-border-black">
                  {item.lengthLined ?? "NULL"}
                </TableCell>

                {/* <TableCell className="tw-border tw-border-black">
                        {item.addedClassII.tX1_6 ?? "NULL"}
                      </TableCell> */}
                <TableCell className="tw-border tw-border-black">
                  {item.addedClassI?.designTClassI ?? "NULL"}
                </TableCell>
                <TableCell className="tw-border tw-border-black">
                  {item.addedClassI?.installTClassI ?? "NULL"}
                </TableCell>
                <TableCell className="tw-border tw-border-black">
                  {item.addedClassI?.burstPressureI ?? "NULL"}
                </TableCell>
                <TableCell className="tw-border tw-border-black">
                  {item.vacuumCondition ?? "NULL"}
                </TableCell>
                <TableCell className="">
                  <div className="tw-flex tw-space-x-2 tw-justify-center">
                    {/* Review Button */}
                    <div className="tw-inline-block">
                      <button
                        onClick={() => handleReview(item.requestData, item._id)}
                        className="tw-text-white tw-py-1 tw-px-4 tw-text-xs tw-font-semibold tw-bg-[#00B5ED] tw-shadow-sm hover:tw-bg-primary/90"
                      >
                        <PencilLine className="tw-w-4 tw-h-4 tw-font-bold" />
                      </button>
                    </div>

                    {/* Delete Button */}
                    <AlertDialog
                      key={item._id}
                      open={isDialogOpen && deleteId === item._id}
                      onOpenChange={(open) => {
                        if (!open) {
                          setTimeout(() => {
                            setDeleteId(null);
                          }, 200);
                        }
                        setIsDialogOpen(open);
                      }}
                    >
                      <AlertDialogTrigger asChild>
                        <button
                          onClick={() => {
                            setDeleteId(item._id);
                            setIsDialogOpen(true);
                          }}
                          className="tw-text-white tw-py-1 tw-px-4 tw-text-xs tw-font-semibold tw-bg-destructive tw-shadow-sm hover:tw-bg-destructive/90"
                        >
                          <Trash2 className="tw-w-4 tw-h-4 tw-font-bold" />
                        </button>
                      </AlertDialogTrigger>

                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Confirm Deletion</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete this result
                            permanently? This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>

                        <AlertDialogFooter>
                          <AlertDialogCancel
                            className="tw-border-2"
                            onClick={handleCancelDelete}
                          >
                            Cancel
                          </AlertDialogCancel>

                          <AlertDialogAction
                            className="tw-bg-destructive mb-2"
                            onClick={handleConfirmDelete}
                          >
                            Confirm
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <p className="tw-text-xs tw-border-x tw-border-b tw-p-4 tw-border-black">
          <strong>NOTES:</strong>
          <ul>
            <li>
              Liner thickness is based on ASTM F1216 Non-Mandatory Design
              Appendix X1.
            </li>
            <li>
              Class I design is to bridge a 1 inch (25 mm) max circular hole in
              the pipe wall.
            </li>
            <li>Max. Liner thickness for NSF61 is 0.55 inch (14 mm).</li>
            {/* <li>
                    Final resin volume includes 10% allowance and approximate number
                    of drums reported
                  </li> */}
          </ul>
        </p>
      </div>

      {/* Class II Design Table */}
      <div className="tw-mb-12">
        <h2 className="tw-text-sm md:tw-text-base tw-font-bold tw-bg-[#00B5ED] tw-text-white tw-p-2 tw-uppercase tw-mb-2 tw-rounded-t-md">
          Class II Design
        </h2>
        <Table className="tw-pb-4 tw-border-collapse tw-border tw-border-black">
          <TableHeader>
            <TableRow className="tw-border tw-border-black">
            <TableHead className="tw-border tw-border-black">
                Project Title
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                Client Name
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                Project Title
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                Client Name
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                Pipe Section No.
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                Pipe ID (in)
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                SIPP Liner Length (ft)
              </TableHead>

              <TableHead className="tw-border tw-border-black">
                t for X1.6 (mm)
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                Design t (mm)
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                Install t (mm)
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                Short-term Burst Pressure
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                Design Assumes
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {designs.map((item, index) => (
              <TableRow key={index} className="tw-border tw-border-black">
                <TableCell className="tw-border tw-border-black">
                  {projectMap[item.project]?.title || "Loading..."}
                </TableCell>

                <TableCell className="tw-border tw-border-black">
                  {projectMap[item.project]?.clientName || "Loading..."}
                </TableCell>
                <TableCell className="tw-border tw-border-black">
                  {index + 1}
                </TableCell>
                <TableCell className="tw-border tw-border-black">
                  {item.internalDiameter ?? "NULL"}
                </TableCell>
                <TableCell className="tw-border tw-border-black">
                  {item.lengthLined ?? "NULL"}
                </TableCell>

                <TableCell className="tw-border tw-border-black">
                  {item.addedClassII.tX1_6 ?? "NULL"}
                </TableCell>
                <TableCell className="tw-border tw-border-black">
                  {item.addedClassII.designTClassII ?? "NULL"}
                </TableCell>
                <TableCell className="tw-border tw-border-black">
                  {item.addedClassII.installTClassII ?? "NULL"}
                </TableCell>
                <TableCell className="tw-border tw-border-black">
                  {item.addedClassII.burstPressureII ?? "NULL"}
                </TableCell>
                <TableCell className="tw-border tw-border-black">
                  {item.vacuumCondition ?? "NULL"}
                </TableCell>
                <TableCell className="">
                  <div className="tw-flex tw-space-x-2 tw-justify-center">
                    {/* Review Button */}
                    <div className="tw-inline-block">
                      <button
                        onClick={() => handleReview(item.requestData, item._id)}
                        className="tw-text-white tw-py-1 tw-px-4 tw-text-xs tw-font-semibold tw-bg-[#00B5ED] tw-shadow-sm hover:tw-bg-primary/90"
                      >
                        <PencilLine className="tw-w-4 tw-h-4 tw-font-bold" />
                      </button>
                    </div>

                    {/* Delete Button */}
                    <AlertDialog
                      key={item._id}
                      open={isDialogOpen && deleteId === item._id}
                      onOpenChange={(open) => {
                        if (!open) {
                          setTimeout(() => {
                            setDeleteId(null);
                          }, 200);
                        }
                        setIsDialogOpen(open);
                      }}
                    >
                      <AlertDialogTrigger asChild>
                        <button
                          onClick={() => {
                            setDeleteId(item._id);
                            setIsDialogOpen(true);
                          }}
                          className="tw-text-white tw-py-1 tw-px-4 tw-text-xs tw-font-semibold tw-bg-destructive tw-shadow-sm hover:tw-bg-destructive/90"
                        >
                          <Trash2 className="tw-w-4 tw-h-4 tw-font-bold" />
                        </button>
                      </AlertDialogTrigger>

                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Confirm Deletion</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete this result
                            permanently? This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>

                        <AlertDialogFooter>
                          <AlertDialogCancel
                            className="tw-border-2"
                            onClick={handleCancelDelete}
                          >
                            Cancel
                          </AlertDialogCancel>

                          <AlertDialogAction
                            className="tw-bg-destructive mb-2"
                            onClick={handleConfirmDelete}
                          >
                            Confirm
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <p className="tw-text-xs tw-border-x tw-border-b tw-p-4 tw-border-black">
          <strong>NOTES:</strong>
          <ul>
            <li>
              Liner thickness is based on ASTM F1216 Non-Mandatory Design
              Appendix X1.
            </li>
            <li>
              Class II design is to bridge a 1 inch (25 mm) max circular hole in
              the pipe wall.
            </li>
            <li>Max. Liner thickness for NSF61 is 0.55 inch (14 mm).</li>
            {/* <li>
              Final resin volume includes 10% allowance and approximate number
              of drums reported
            </li> */}
          </ul>
        </p>
      </div>

      {/* Class III Design Table */}
      <div className="tw-mb-12">
        <h2 className="tw-text-sm md:tw-text-base tw-font-bold tw-bg-[#00B5ED] tw-text-white tw-p-2 tw-uppercase tw-mb-2 tw-rounded-t-md">
          Class III Design
        </h2>
        <Table className="tw-pb-4 tw-border-collapse tw-border tw-border-black">
          <TableHeader>
            <TableRow className="tw-border tw-border-black">
              <TableHead className="tw-border tw-border-black">
                Project Title
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                Client Name
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                Pipe Section No.
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                Pipe ID (in)
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                SIPP Liner Length (ft)
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                t for X1.1 (mm)
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                t for X1.6/X1.7 (mm)
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                Design t (mm)
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                Install t (mm)
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                Short-term Burst Pressure
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                Design Assumes
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {designs.map((item, index) => (
              <TableRow key={index} className="tw-border tw-border-black">
                <TableCell className="tw-border tw-border-black">
                  {projectMap[item.project]?.title || "Loading..."}
                </TableCell>

                <TableCell className="tw-border tw-border-black">
                  {projectMap[item.project]?.clientName || "Loading..."}
                </TableCell>
                <TableCell className="tw-border tw-border-black">
                  {index + 1}
                </TableCell>
                <TableCell className="tw-border tw-border-black">
                  {item.internalDiameter ?? "NULL"}
                </TableCell>
                <TableCell className="tw-border tw-border-black">
                  {item.lengthLined ?? "NULL"}
                </TableCell>
                <TableCell className="tw-border tw-border-black">
                  {item.partiallyDeteriorated.tX1_1 ?? "NULL"}
                </TableCell>
                <TableCell className="tw-border tw-border-black">
                  {item.partiallyDeteriorated.tX1_6 ?? "NULL"}
                </TableCell>
                <TableCell className="tw-border tw-border-black">
                  {item.partiallyDeteriorated.partiallyDesignThickness ??
                    "NULL"}
                </TableCell>
                <TableCell className="tw-border tw-border-black">
                  {item.partiallyDeteriorated.partiallyInstallThickness ??
                    "NULL"}
                </TableCell>
                <TableCell className="tw-border tw-border-black">
                  {item.partiallyDeteriorated.burstPressureIII ?? "NULL"}
                </TableCell>
                <TableCell className="tw-border tw-border-black">
                  {item.vacuumCondition ?? "NULL"}
                </TableCell>
                <TableCell className="">
                  <div className="tw-flex tw-space-x-2 tw-justify-center">
                    {/* Review Button */}
                    <div className="tw-inline-block">
                      <button
                        onClick={() => handleReview(item.requestData, item._id)}
                        className="tw-text-white tw-py-1 tw-px-4 tw-text-xs tw-font-semibold tw-bg-[#00B5ED] tw-shadow-sm hover:tw-bg-primary/90"
                      >
                        <PencilLine className="tw-w-4 tw-h-4 tw-font-bold" />
                      </button>
                    </div>

                    {/* Delete Button */}
                    <AlertDialog
                      key={item._id}
                      open={isDialogOpen && deleteId === item._id}
                      onOpenChange={(open) => {
                        if (!open) {
                          setTimeout(() => {
                            setDeleteId(null);
                          }, 200);
                        }
                        setIsDialogOpen(open);
                      }}
                    >
                      <AlertDialogTrigger asChild>
                        <button
                          onClick={() => {
                            setDeleteId(item._id);
                            setIsDialogOpen(true);
                          }}
                          className="tw-text-white tw-py-1 tw-px-4 tw-text-xs tw-font-semibold tw-bg-destructive tw-shadow-sm hover:tw-bg-destructive/90"
                        >
                          <Trash2 className="tw-w-4 tw-h-4 tw-font-bold" />
                        </button>
                      </AlertDialogTrigger>

                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Confirm Deletion</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete this result
                            permanently? This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>

                        <AlertDialogFooter>
                          <AlertDialogCancel
                            className="tw-border-2"
                            onClick={handleCancelDelete}
                          >
                            Cancel
                          </AlertDialogCancel>

                          <AlertDialogAction
                            className="tw-bg-destructive mb-2"
                            onClick={handleConfirmDelete}
                          >
                            Confirm
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <p className="tw-text-xs tw-border-x tw-border-b tw-p-4 tw-border-black">
          <strong>NOTES:</strong>
          <ul>
            <li>
              Liner thickness is based on ASTM F1216 Non-Mandatory Design
              Appendix X1.
            </li>
            <li>
              Class III design is to bridge a 1 inch (25 mm) max circular hole
              in the pipe wall.
            </li>
            <li>Max. Liner thickness for NSF61 is 0.55 inch (14 mm).</li>
            {/* <li>
              Final resin volume includes 10% allowance and approximate number
              of drums reported
            </li> */}
          </ul>
        </p>
      </div>

      {/* Class IV Design Table */}
      <div className="tw-mb-6">
        <h2 className="tw-text-sm md:tw-text-base tw-font-bold tw-bg-[#00B5ED] tw-text-white tw-p-2 tw-uppercase tw-mb-2 tw-rounded-t-md">
          Class IV Design
        </h2>
        <Table className="tw-pb-4 tw-border-collapse tw-border tw-border-black">
          <TableHeader>
            <TableRow className="tw-border tw-border-black">
              <TableHead className="tw-border tw-border-black">
                Project Title
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                Client Name
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                Pipe Section No.
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                Pipe ID (in)
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                SIPP Liner Length (ft)
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                t for X1.1 (mm)
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                t for X1.3 (mm)
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                t for X1.7 (mm)
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                Design t (mm)
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                Install t (mm)
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                Short-term Burst Pressure
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                Design Assumes
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {designs.map((item, index) => (
              <TableRow className="tw-border tw-border-black" key={index}>
                <TableCell className="tw-border tw-border-black">
                  {projectMap[item.project]?.title || "Loading..."}
                </TableCell>

                <TableCell className="tw-border tw-border-black">
                  {projectMap[item.project]?.clientName || "Loading..."}
                </TableCell>
                <TableCell className="tw-border tw-border-black">
                  {index + 1}
                </TableCell>
                <TableCell className="tw-border tw-border-black">
                  {item.internalDiameter ?? "NULL"}
                </TableCell>
                <TableCell className="tw-border tw-border-black">
                  {item.lengthLined ?? "NULL"}
                </TableCell>
                <TableCell
                  className={`tw-border tw-border-black ${
                    item.fullyDeteriorated.tX1_1 > 14 ? "tw-bg-[#FFBF00]" : ""
                  }`}
                >
                  {item.fullyDeteriorated.tX1_1 ?? "NULL"}
                </TableCell>
                <TableCell className="tw-border tw-border-black">
                  {item.fullyDeteriorated.tX1_3 ?? "NULL"}
                </TableCell>
                <TableCell className="tw-border tw-border-black">
                  {item.fullyDeteriorated.tX1_7 ?? "NULL"}
                </TableCell>
                <TableCell className="tw-border tw-border-black">
                  {item.fullyDeteriorated.fullyDesignThickness ?? "NULL"}
                </TableCell>
                <TableCell className="tw-border tw-border-black">
                  {item.fullyDeteriorated.fullyInstallThickness ?? "NULL"}
                </TableCell>
                <TableCell className="tw-border tw-border-black">
                  {item.fullyDeteriorated.burstPressureIV ?? "NULL"}
                </TableCell>
                <TableCell className="tw-border tw-border-black">
                  {item.vacuumCondition ?? "NULL"}
                </TableCell>
                <TableCell className="">
                  <div className="tw-flex tw-space-x-2 tw-justify-center">
                    {/* Review Button */}
                    <div className="tw-inline-block">
                      <button
                        onClick={() => handleReview(item.requestData, item._id)}
                        className="tw-text-white tw-py-1 tw-px-4 tw-text-xs tw-font-semibold tw-bg-[#00B5ED] tw-shadow-sm hover:tw-bg-primary/90"
                      >
                        <PencilLine className="tw-w-4 tw-h-4 tw-font-bold" />
                      </button>
                    </div>

                    {/* Delete Button */}
                    <AlertDialog
                      key={item._id}
                      open={isDialogOpen && deleteId === item._id}
                      onOpenChange={(open) => {
                        if (!open) {
                          setTimeout(() => {
                            setDeleteId(null);
                          }, 200);
                        }
                        setIsDialogOpen(open);
                      }}
                    >
                      <AlertDialogTrigger asChild>
                        <button
                          onClick={() => {
                            setDeleteId(item._id);
                            setIsDialogOpen(true);
                          }}
                          className="tw-text-white tw-py-1 tw-px-4 tw-text-xs tw-font-semibold tw-bg-destructive tw-shadow-sm hover:tw-bg-destructive/90"
                        >
                          <Trash2 className="tw-w-4 tw-h-4 tw-font-bold" />
                        </button>
                      </AlertDialogTrigger>

                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Confirm Deletion</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete this result
                            permanently? This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>

                        <AlertDialogFooter>
                          <AlertDialogCancel
                            className="tw-border-2"
                            onClick={handleCancelDelete}
                          >
                            Cancel
                          </AlertDialogCancel>

                          <AlertDialogAction
                            className="tw-bg-destructive mb-2"
                            onClick={handleConfirmDelete}
                          >
                            Confirm
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <p className="tw-text-xs tw-border-x tw-border-b tw-p-4 tw-border-black">
          <strong>NOTES:</strong>
          <ul>
            <li>
              Liner thickness is based on ASTM F1216 Non-Mandatory Design
              Appendix X1.
            </li>
            <li>
              Class IV is only to meet the pipe operational pressure
              requirements.
            </li>
            <li>Max. Liner thickness for NSF61 is 0.55 inch (14 mm).</li>
            {/* <li>
              Final resin volume includes 10% allowance and approximate number
              of drums reported
            </li> */}
          </ul>
        </p>
      </div>
    </main>
  );
};

export default UserDesign;
